import React from "react";
import { Dialog } from "../../../../ui/components/Dialog";
import { Button } from "../../../../ui/components/Button";
import { FeatherRefreshCw, FeatherAlertTriangle } from "@subframe/core";

export interface VendorRerunConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the user confirms the re-run. */
  onConfirm: () => void;
  /** Vendor currently on the bill (shown as the "from"). Empty = first-time assignment. */
  oldVendorName?: string | null;
  /** Newly selected vendor (shown as the "to"). */
  newVendorName?: string | null;
  /** True while the re-run is in flight — switches the modal to progress view. */
  loading?: boolean;
  /** Set when the re-run fails — switches the modal to the error view. */
  error?: string | null;
}

/**
 * Confirmation + progress shown when an accountant sets/changes the vendor on a bill.
 *
 * Setting the vendor runs the post-match pipeline (GL + tax + TDS). Two framings:
 *   - first-time  (no prior vendor / unmatched bill) → "classify" wording, no
 *     overwrite warning (there's nothing to overwrite yet).
 *   - change      (vendor already on the bill)       → "re-run" wording + warning
 *     that current classifications and manual edits will be overwritten.
 *
 * Three visual states driven by `loading` / `error`:
 *   1. confirm  — framing-specific message + Cancel / action button
 *   2. progress — spinner (no dismiss; the page reloads on success, closing this)
 *   3. error    — failure message + Close
 */
export const VendorRerunConfirmModal: React.FC<VendorRerunConfirmModalProps> = ({
  open,
  onOpenChange,
  onConfirm,
  oldVendorName,
  newVendorName,
  loading = false,
  error = null,
}) => {
  const isFirstTime = !oldVendorName;

  const confirmTitle = isFirstTime
    ? "Classify this bill for the selected vendor?"
    : "Re-run classifications for this bill?";
  const confirmButtonLabel = isFirstTime ? "Classify bill" : "Re-run pipeline";
  const progressTitle = isFirstTime
    ? "Classifying this bill…"
    : "Re-processing vendor…";
  const headerCircle = isFirstTime ? "bg-brand-100" : "bg-warning-100";
  const headerIcon = isFirstTime ? "text-brand-600" : "text-warning-600";
  const confirmBox = isFirstTime
    ? "border-neutral-200 bg-neutral-50"
    : "border-warning-200 bg-warning-50";

  const vendorSummary = (oldVendorName || newVendorName) && (
    <div className="flex w-full items-center gap-2 rounded-md border border-solid border-neutral-200 bg-neutral-50 px-4 py-3">
      {isFirstTime ? (
        <>
          <span className="text-body font-body text-neutral-400">Vendor:</span>
          <span className="text-body font-body-bold text-brand-700 line-clamp-1">
            {newVendorName || "—"}
          </span>
        </>
      ) : (
        <>
          <span className="text-body font-body-bold text-neutral-700 line-clamp-1">
            {oldVendorName || "—"}
          </span>
          <span className="text-body font-body text-neutral-400">→</span>
          <span className="text-body font-body-bold text-brand-700 line-clamp-1">
            {newVendorName || "—"}
          </span>
        </>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="min-w-[440px] max-w-[540px]">
        <div className="flex w-full flex-col items-start gap-6 px-6 py-6">
          {error ? (
            // ---- 3. Error ---------------------------------------------------
            <>
              <div className="flex w-full items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-error-100">
                  <FeatherAlertTriangle className="h-5 w-5 text-error-600" />
                </div>
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Re-run failed
                </span>
              </div>
              <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-error-200 bg-error-50 px-4 py-4">
                <span className="text-body font-body text-neutral-700 leading-relaxed">
                  {error}
                </span>
              </div>
              <div className="flex w-full items-center justify-end gap-3 pt-1">
                <Button
                  variant="neutral-secondary"
                  onClick={() => onOpenChange(false)}
                >
                  Close
                </Button>
              </div>
            </>
          ) : loading ? (
            // ---- 2. Progress ------------------------------------------------
            <>
              <div className="flex w-full items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-100">
                  <FeatherRefreshCw className="h-5 w-5 text-brand-600 animate-spin" />
                </div>
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {progressTitle}
                </span>
              </div>
              {vendorSummary}
              <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-200 bg-neutral-50 px-4 py-4">
                <span className="text-body font-body text-neutral-700 leading-relaxed">
                  Running{" "}
                  <span className="font-body-bold">
                    GL, tax (IGST / CGST+SGST) and TDS
                  </span>{" "}
                  for the selected vendor. This usually takes a few seconds — the
                  page refreshes automatically when it's done.
                </span>
              </div>
            </>
          ) : (
            // ---- 1. Confirm -------------------------------------------------
            <>
              <div className="flex w-full items-center gap-3">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${headerCircle}`}
                >
                  <FeatherRefreshCw className={`h-5 w-5 ${headerIcon}`} />
                </div>
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {confirmTitle}
                </span>
              </div>
              {vendorSummary}
              <div
                className={`flex w-full flex-col items-start gap-2 rounded-md border border-solid px-4 py-4 ${confirmBox}`}
              >
                <span className="text-body font-body text-neutral-700 leading-relaxed">
                  {isFirstTime ? (
                    <>
                      This runs{" "}
                      <span className="font-body-bold">
                        GL classification, tax (IGST / CGST+SGST) and TDS
                      </span>{" "}
                      for the selected vendor for the first time. The extracted
                      invoice data is not changed.
                    </>
                  ) : (
                    <>
                      This re-runs{" "}
                      <span className="font-body-bold">
                        GL classification, tax (IGST / CGST+SGST) and TDS
                      </span>{" "}
                      for the new vendor and overwrites the bill's current
                      classifications,{" "}
                      <span className="font-body-bold">
                        including any manual edits you've made
                      </span>
                      . The extracted invoice data is not changed.
                    </>
                  )}
                </span>
              </div>
              <div className="flex w-full items-center justify-end gap-3 pt-1">
                <Button
                  variant="neutral-secondary"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="brand-primary"
                  icon={<FeatherRefreshCw />}
                  onClick={onConfirm}
                >
                  {confirmButtonLabel}
                </Button>
              </div>
            </>
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

export default VendorRerunConfirmModal;
